import * as React from 'react';
import {
  ActivityIcon,
  BookOpenIcon,
  CodeIcon,
  FileBarChartIcon,
  UsersIcon
} from 'lucide-react';

import { AUDIENCE_RESEARCH_NAME } from '@workspace/common/app';

const DOCS_LINK_GROUPS = [
  {
    title: 'Start here',
    icon: <BookOpenIcon className="size-4 shrink-0 text-muted-foreground" />,
    items: [
      {
        title: 'Lumnis overview',
        href: '/docs',
        description: 'Understand the main ways to use Lumnis.',
        keywords: ['overview', 'product', 'market intelligence'],
        items: [] as never[]
      },
      {
        title: 'Launch your first prospecting project',
        href: '/docs/getting-started',
        description: 'Create a Persona, choose a search type, and launch.',
        keywords: ['quick start', 'prospecting project', 'first search'],
        items: [] as never[]
      }
    ]
  },
  {
    title: 'People intelligence',
    icon: <UsersIcon className="size-4 shrink-0 text-muted-foreground" />,
    items: [
      {
        title: 'Personas',
        href: '/docs/personas',
        description:
          'Define and reuse the people and companies you care about.',
        keywords: ['ICP', 'criteria', 'audience', 'target buyer'],
        items: [] as never[]
      },
      {
        title: 'Search types',
        href: '/docs/search-types',
        description: 'Choose the right source for the question you are asking.',
        keywords: [
          'high intent',
          'job signal',
          'competitor',
          'Sales Navigator'
        ],
        items: [] as never[]
      },
      {
        title: 'Search examples',
        href: '/docs/search-examples',
        description:
          'Use focused prompts, public evidence, and clear review steps.',
        keywords: [
          'examples',
          'best practices',
          'web research',
          'buying signals'
        ],
        items: [] as never[]
      },
      {
        title: 'Projects',
        href: '/docs/projects',
        description: 'Open a prospect list and its related campaigns.',
        keywords: ['results', 'prospects', 'fit', 'evidence', 'campaigns'],
        items: [] as never[]
      }
    ]
  },
  {
    title: 'Companies and markets',
    icon: (
      <FileBarChartIcon className="size-4 shrink-0 text-muted-foreground" />
    ),
    items: [
      {
        title: 'Account Intelligence',
        href: '/docs/account-intelligence',
        description: 'Research a company and find the people who matter.',
        keywords: ['accounts', 'company research', 'buying committee'],
        items: [] as never[]
      },
      {
        title: AUDIENCE_RESEARCH_NAME,
        href: '/docs/audience-research',
        description: 'See the posts, themes, and language earning attention.',
        keywords: ['brand', 'content', 'topics', 'post ideas'],
        items: [] as never[]
      }
    ]
  },
  {
    title: 'Run your workspace',
    icon: <ActivityIcon className="size-4 shrink-0 text-muted-foreground" />,
    items: [
      {
        title: 'Set up your first campaign',
        href: '/docs/campaigns',
        description: 'Choose channels, build a playbook, and launch outreach.',
        keywords: [
          'campaign',
          'outreach',
          'launch',
          'email',
          'LinkedIn',
          'playbook'
        ],
        items: [] as never[]
      },
      {
        title: 'Pipelines and Approvals',
        href: '/docs/pipelines-and-approvals',
        description:
          'Run a weekly search and review actions before they continue.',
        keywords: ['weekly', 'recurring', 'approve', 'reject', 'campaign'],
        items: [] as never[]
      },
      {
        title: 'Integrations',
        href: '/docs/integrations',
        description:
          'Connect LinkedIn, your calendar, email, and CRM workspace.',
        keywords: ['HubSpot', 'Attio', 'Gmail', 'Outlook', 'MCP'],
        items: [] as never[]
      },
      {
        title: 'Lumnis MCP',
        href: '/docs/lumnis-mcp',
        description:
          'Connect your AI assistant and explore every intelligence and workflow function.',
        keywords: ['MCP', 'Claude', 'Cursor', 'tools', 'Person Intelligence'],
        items: [] as never[]
      },
      {
        title: 'Credits and plans',
        href: '/docs/credits-and-plans',
        description:
          'Understand credit previews, search sizes, and plan limits.',
        keywords: ['billing', 'free', 'usage', 'search size'],
        items: [] as never[]
      }
    ]
  },
  {
    title: 'Developer API · Preview',
    // Preserve these entries for launch without promoting the preview guides.
    hidden: true,
    icon: <CodeIcon className="size-4 shrink-0 text-muted-foreground" />,
    items: [
      {
        title: 'Developer API quick start',
        href: '/docs/quick-start',
        description: 'Preserved setup guide for the future developer platform.',
        keywords: ['API key', 'developer', 'preview'],
        items: [] as never[]
      },
      {
        title: 'REST API',
        href: '/docs/api',
        description: 'Preserved endpoint reference for the future API launch.',
        keywords: ['REST', 'HTTP', 'endpoint'],
        items: [] as never[]
      },
      {
        title: 'Python SDK overview',
        href: '/docs/python-sdk',
        description: 'Installation and client overview for Python.',
        keywords: ['Python', 'pip', 'SDK'],
        items: [] as never[]
      },
      {
        title: 'Python responses',
        href: '/docs/python-sdk-responses',
        description: 'Create and inspect responses with Python.',
        keywords: ['Python', 'responses'],
        items: [] as never[]
      },
      {
        title: 'Python users',
        href: '/docs/python-sdk-users',
        description: 'Manage developer-platform users with Python.',
        keywords: ['Python', 'users'],
        items: [] as never[]
      },
      {
        title: 'Python files',
        href: '/docs/python-sdk-files',
        description: 'Upload and search files with Python.',
        keywords: ['Python', 'files'],
        items: [] as never[]
      },
      {
        title: 'Python integrations',
        href: '/docs/python-sdk-integrations',
        description: 'Connect external services from the Python client.',
        keywords: ['Python', 'OAuth', 'integrations'],
        items: [] as never[]
      },
      {
        title: 'Python MCP servers',
        href: '/docs/python-sdk-mcp-servers',
        description: 'Configure MCP servers through the Python client.',
        keywords: ['Python', 'MCP'],
        items: [] as never[]
      },
      {
        title: 'Python model preferences',
        href: '/docs/python-sdk-model-preferences',
        description: 'Preserved model preference reference.',
        keywords: ['Python', 'models'],
        items: [] as never[]
      },
      {
        title: 'Python external API keys',
        href: '/docs/python-sdk-api-keys',
        description: 'Preserved bring-your-own-key reference.',
        keywords: ['Python', 'API keys'],
        items: [] as never[]
      },
      {
        title: 'Python advanced usage',
        href: '/docs/python-sdk-advanced',
        description: 'Errors, testing, and advanced Python patterns.',
        keywords: ['Python', 'errors', 'testing'],
        items: [] as never[]
      },
      {
        title: 'JavaScript SDK overview',
        href: '/docs/javascript-sdk',
        description: 'Installation and client overview for JavaScript.',
        keywords: ['JavaScript', 'TypeScript', 'Node.js'],
        items: [] as never[]
      },
      {
        title: 'JavaScript responses',
        href: '/docs/javascript-sdk-responses',
        description: 'Create and inspect responses with JavaScript.',
        keywords: ['JavaScript', 'responses'],
        items: [] as never[]
      },
      {
        title: 'JavaScript users',
        href: '/docs/javascript-sdk-users',
        description: 'Manage developer-platform users with JavaScript.',
        keywords: ['JavaScript', 'users'],
        items: [] as never[]
      },
      {
        title: 'JavaScript integrations',
        href: '/docs/javascript-sdk-integrations',
        description: 'Connect external services from the JavaScript client.',
        keywords: ['JavaScript', 'OAuth', 'integrations'],
        items: [] as never[]
      },
      {
        title: 'Lovable guide',
        href: '/docs/using-lovable',
        description: 'Preserved Lovable integration walkthrough.',
        keywords: ['Lovable', 'website', 'developer'],
        items: [] as never[]
      }
    ]
  }
];

// Sidebar, search, and previous/next links share this public navigation.
export const DOCS_LINKS = DOCS_LINK_GROUPS.filter((group) => !group.hidden);
